import { isFilled } from "@prismicio/helpers"
import { ImageField } from "@prismicio/types"
import classNames from "classnames"
import { ImgixURLParams, buildURL } from "imgix-url-builder"
import { default as Image, ImageLoaderProps, ImageProps } from "next/image"
import { useEffect, useRef, useState } from "react"

export const PrismicImage = ({ className, img, sizes }: { className?: string; img: ImageField; sizes?: string }) =>
    isFilled.image(img) ? (
        <NextImage
            className={className}
            alt={img.alt || ""}
            src={img.url}
            sizes={sizes}
            width={img.dimensions.width}
            height={img.dimensions.height}
        />
    ) : (
        <></>
    )
export const LcpImage = ({
    alt,
    ...props
}: Omit<ImageProps, "src" | "priority" | "loading"> & {
    src: string
}) => {
    const staticImage = /^\//.test(props.src)
    return (
        <Image
            alt={alt || ""}
            loading="eager"
            priority
            loader={
                staticImage
                    ? undefined
                    : (args: ImageLoaderProps) => {
                          const staticImage = /^\//.test(args.src)
                          if (staticImage) return args.src
                          const url = new URL(args.src)
                          const params: ImgixURLParams = {
                              fit: (url.searchParams.get("fit") as ImgixURLParams["fit"]) || "max",
                              w: args.width,
                              h: undefined,
                          }
                          if (args.quality) {
                              params.q = args.quality
                          }
                          return buildURL(args.src, params)
                      }
            }
            {...props}
        />
    )
}

const LazyImage = ({
    alt,
    onLoad,
    className,
    blur = true,
    fill,
    width,
    height,
    ...props
}: Omit<ImageProps, "blur" | "src" | "priority" | "loading" | "loader"> & { src: string; blur?: boolean }) => {
    const [loaded, setLoaded] = useState(false)
    const image = useRef<HTMLImageElement | null>(null)
    useEffect(() => {
        if (image.current?.complete) setLoaded(true)
    }, [])
    const staticImage = /^\//.test(props.src)
    return (
        <Image
            width={fill ? undefined : width}
            height={fill ? undefined : height}
            fill={fill}
            ref={image}
            alt={alt || ""}
            className={classNames({ blur }, blur ? (loaded ? "loaded" : "pending") : "", className)}
            loading="lazy"
            onLoad={(e) => {
                onLoad?.(e)
                setLoaded(true)
            }}
            loader={
                staticImage
                    ? undefined
                    : (args: ImageLoaderProps) => {
                          const staticImage = /^\//.test(args.src)
                          if (staticImage) {
                              return args.src
                          }
                          const url = new URL(args.src)
                          const params: ImgixURLParams = {
                              fit: (url.searchParams.get("fit") as ImgixURLParams["fit"]) || "max",
                              w: args.width,
                              h: undefined,
                          }

                          if (args.quality) {
                              params.q = args.quality
                          }
                          return buildURL(args.src, params)
                      }
            }
            {...props}
        />
    )
}
export const NextImage = ({
    lcp = false,
    ...props
}: Omit<ImageProps, "blur" | "src" | "priority" | "loading"> & { src: string; blur?: boolean; lcp?: boolean }) => {
    if (lcp) {
        return <LcpImage {...props} />
    }
    return <LazyImage {...props} />
}
